import React from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Alert,
} from "@mui/material";
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useNavigate, useSearchParams } from "react-router-dom";
import useQuestionStore from "../../store/useQuestionStore";
import { getFacultyCourses } from "../../services/courses.api";

const statusChipProps = (status) => {
  switch (status) {
    case "ACTIVE":
      return { label: "Active", color: "success" };
    case "INACTIVE":
      return { label: "Inactive", color: "default" };
    default:
      return { label: status, color: "default" };
  }
};

const difficultyLabel = (difficulty) => {
  if (!difficulty) return "";
  const normalized = String(difficulty).trim().toUpperCase();
  if (normalized === "EASY") return "Easy";
  if (normalized === "MEDIUM") return "Medium";
  if (normalized === "HARD") return "Hard";
  return difficulty;
};

const FacultyQuestionBankPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qpCourseId = searchParams.get("courseId") || "";

  const questions = useQuestionStore((s) => s.questions);
  const safeQuestions = Array.isArray(questions) ? questions : [];
  const loading = useQuestionStore((s) => s.loading);
  const error = useQuestionStore((s) => s.error);
  const fetchAll = useQuestionStore((s) => s.fetchAllQuestions);

  const selectedCourseId = useQuestionStore((s) => s.selectedCourseId);
  const setSelectedCourseId = useQuestionStore((s) => s.setSelectedCourseId);

  const [courses, setCourses] = React.useState([]);
  const [coursesLoading, setCoursesLoading] = React.useState(true);
  const [coursesError, setCoursesError] = React.useState("");

  // URL -> store (mirror always, including clearing)
  React.useEffect(() => {
    if (qpCourseId !== selectedCourseId) setSelectedCourseId(qpCourseId);
  }, [qpCourseId, selectedCourseId, setSelectedCourseId]);

  // fetch
  React.useEffect(() => {
    if (!selectedCourseId) return;
    fetchAll({ courseId: selectedCourseId });
  }, [fetchAll, selectedCourseId]);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      setCoursesLoading(true);
      setCoursesError("");
      try {
        const data = await getFacultyCourses();
        if (!mounted) return;
        setCourses(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setCoursesError(e?.response?.data?.message || "Failed to load courses");
      } finally {
        if (mounted) setCoursesLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  const handleCourseChange = (e) => {
    const next = e.target.value || "";
    setSelectedCourseId(next);
    if (next) setSearchParams({ courseId: next }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const showSelectCourseMsg = !selectedCourseId && !coursesLoading && !coursesError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Question Bank
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
          <TextField
            select
            size="small"
            label="Course"
            value={selectedCourseId}
            onChange={handleCourseChange}
            sx={{ minWidth: 260 }}
            disabled={coursesLoading || !!coursesError}
          >
            <MenuItem value="">
              <em>Select course</em>
            </MenuItem>
            {courses.map((c) => (
              <MenuItem key={c._id} value={c.courseId}>
                {c.courseId} — {c.title}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            disabled={!selectedCourseId}
            onClick={() => navigate(`/faculty/questions/new?courseId=${encodeURIComponent(selectedCourseId)}`)}
          >
            Add Question
          </Button>
        </Stack>
      </Stack>

      {coursesError && <Alert severity="error" sx={{ mb: 2 }}>{coursesError}</Alert>}
      {showSelectCourseMsg && <Alert severity="info" sx={{ mb: 2 }}>Select a course to view its questions.</Alert>}

      <Paper elevation={1} sx={{ p: 2 }}>
        {selectedCourseId && loading && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Loading questions...
          </Typography>
        )}
        {selectedCourseId && error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : error?.message || 'Something went wrong'}
          </Typography>
        )}

        {!selectedCourseId ? (
          <Typography variant="body2" color="text.secondary">
            No course selected.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="medium" aria-label="question bank table">
              <TableHead>
                <TableRow>
                  <TableCell>Question ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Difficulty</TableCell>
                  <TableCell>Mongo Marks</TableCell>
                  <TableCell>Node Marks</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {safeQuestions.map((question) => {
                  const questionKey = question._id || question.id || question.questionId;
                  const status = question.status || (question.isActive ? "ACTIVE" : "INACTIVE");
                  return (
                    <TableRow key={questionKey} hover>
                      <TableCell>{question.questionId || question.id}</TableCell>
                      <TableCell>{question.title}</TableCell>
                      <TableCell>{difficultyLabel(question.difficulty)}</TableCell>
                      <TableCell>{question.parts?.part1?.maxMarks ?? "-"}</TableCell>
                      <TableCell>{question.parts?.part2?.maxMarks ?? "-"}</TableCell>
                      <TableCell>
                        <Chip size="small" {...statusChipProps(status)} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/faculty/questions/${questionKey}/edit?courseId=${encodeURIComponent(selectedCourseId)}`
                              )
                            }
                            aria-label="edit question"
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default FacultyQuestionBankPage;
